import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, postForm } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { ConflictNotice } from '../ui/ConflictNotice';
import './eventFiles.css';
import { FileDrop } from '../ui/FileDrop';

const EVENTS_RESOURCE = 'events.events';
const FILES_RESOURCE = 'files.files';
/** The server's limit of a description (modules/events event-files.ts). */
const DESCRIPTION_MAX = 300;

export type EventFile = {
  id: string;
  /** The file's name in Box. */
  name: string;
  size: number;
  mimeType: string;
  description: string;
  addedBy: string;
  addedByName: string;
  addedAt: string;
  version: number;
};

export const eventFilesKey = (eventId: string) => ['event-files', eventId] as const;

/** „1,2 MB”, „340 kB”, „12 B” (1 MB = 1024 kB, as the 25 MB limit counts). */
export function sizeText(bytes: number): string {
  const fmt = (n: number) => n.toLocaleString('pl-PL', { maximumFractionDigits: 1 });
  if (bytes >= 1024 * 1024) return `${fmt(bytes / (1024 * 1024))} MB`;
  if (bytes >= 1024) return `${fmt(Math.round(bytes / 1024))} kB`;
  return `${bytes} B`;
}

const DAY = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: 'numeric', month: 'numeric', year: 'numeric' });

/** Why „+ Dodaj plik” is off (the server's 422 says the same). */
const NOT_READY = 'Najpierw ustaw folder na pliki wydarzeń w Box (Ustawienia firmy)';

/**
 * „Pliki” in the event card's details (owner 2026-11-04 18:47: „chce przypinac pliki np. rider techniczny do
 * wydarzenia”), kept only in Box (owner 21:49/21:50), each with a mandatory description. The upload goes through the
 * server straight to Box; the download through the program. Hidden without „Pliki i załączniki: Podgląd”; adding needs
 * editing the event and „Dodawanie”, describing and removing editing. Without Box set (its folder for event files)
 * adding is off, with the reason (the owner: a text link to Ustawienia).
 */
export function EventFiles({ eventId }: { eventId: string }) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const mayView = !!me && can(me, EVENTS_RESOURCE, 'view') && can(me, FILES_RESOURCE, 'view');
  const list = useQuery({ queryKey: eventFilesKey(eventId), enabled: mayView, queryFn: () => api<{ files: EventFile[]; storageReady: boolean }>(`/api/events/${eventId}/files`) });
  const refresh = () => queryClient.invalidateQueries({ queryKey: eventFilesKey(eventId) });

  const [adding, setAdding] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);

  const add = useMutation({
    // the description before the file: the server reads it first
    mutationFn: (input: { file: File; description: string }) => {
      const body = new FormData();
      body.append('description', input.description);
      body.append('file', input.file);
      return postForm<EventFile>(`/api/events/${eventId}/files`, body);
    },
    onSuccess: async () => {
      setAdding(false);
      setFile(null);
      setDescription('');
      await refresh();
    },
  });
  const describe = useMutation({
    mutationFn: (input: { f: EventFile; text: string }) => api<EventFile>(`/api/events/${eventId}/files/${input.f.id}`, { method: 'PATCH', body: { description: input.text, version: input.f.version } }),
    onSuccess: async () => {
      setEditing(null);
      remove.reset();
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (f: EventFile) => api<void>(`/api/events/${eventId}/files/${f.id}?version=${f.version}`, { method: 'DELETE' }),
    onSuccess: async () => {
      // review M4: an older failed description edit is no news any more
      describe.reset();
      await refresh();
    },
  });

  if (!me || !mayView) return null;
  const mayEdit = can(me, EVENTS_RESOURCE, 'edit');
  const mayAdd = mayEdit && can(me, FILES_RESOURCE, 'create');
  const files = list.data?.files;
  const notReady = list.data?.storageReady === false;
  const writeError = [describe.error, remove.error].find((e) => e instanceof ApiError) as ApiError | undefined;
  const addError = add.error instanceof ApiError ? add.error : add.error ? new ApiError(0, 'unknown', 'Nie udało się dodać pliku') : null;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (file && description.trim()) add.mutate({ file, description: description.trim() });
  }

  return (
    <section className="event-files" aria-label="Pliki" data-feature-id="events.files">
      <div className="event-files-head">
        <h3>Pliki</h3>
        {mayAdd && !adding && (
          <button className="btn small ghost" type="button" disabled={notReady} onClick={() => setAdding(true)}>
            + Dodaj plik
          </button>
        )}
      </div>
      {mayAdd && notReady && (
        <p className="muted small">
          {me.isOwner ? (
            <>
              Najpierw ustaw folder na pliki wydarzeń w Box (<Link to="/ustawienia">Ustawienia firmy</Link>)
            </>
          ) : (
            NOT_READY
          )}
        </p>
      )}
      {list.isLoading && <p className="muted small" role="status">Wczytywanie…</p>}
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać plików'}</p>}
      {writeError?.status === 409 && (
        <ConflictNotice
          onReload={() => {
            describe.reset();
            remove.reset();
            setEditing(null);
            void refresh();
          }}
        />
      )}
      {writeError && writeError.status !== 409 && <p className="alert" role="alert">{writeError.message}</p>}
      {files && files.length === 0 && !adding && <p className="muted small">Brak plików. Dodaj tu np. rider techniczny albo plan sceny.</p>}
      {files && files.length > 0 && (
        <ul className="event-files-list">
          {files.map((f) => (
            <li key={f.id}>
              {editing?.id === f.id ? (
                <form
                  className="event-files-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    describe.mutate({ f, text: editing.text.trim() });
                  }}
                >
                  <label className="field">
                    <span>Opis pliku {f.name}</span>
                    <input type="text" required maxLength={DESCRIPTION_MAX} value={editing.text} autoFocus onChange={(e) => setEditing({ id: f.id, text: e.target.value })} />
                  </label>
                  <div className="actions">
                    <button className="btn small primary" type="submit" disabled={describe.isPending || !editing.text.trim()}>
                      Zapisz
                    </button>
                    <button className="btn small" type="button" onClick={() => setEditing(null)}>
                      Anuluj
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <span className="event-file-main">
                    <a href={`/api/events/${eventId}/files/${f.id}/content`} download={f.name}>
                      {f.name}
                    </a>
                    {f.description && <span className="event-file-description">{f.description}</span>}
                    <span className="event-file-meta">{[sizeText(f.size), f.addedByName, DAY.format(new Date(f.addedAt))].filter(Boolean).join(' · ')}</span>
                  </span>
                  {mayEdit && (
                    <span className="event-file-actions">
                      <button className="btn small ghost" type="button" aria-label={`Zmień opis: ${f.name}`} onClick={() => setEditing({ id: f.id, text: f.description })}>
                        Zmień opis
                      </button>
                      <button
                        className="btn small ghost"
                        type="button"
                        aria-label={`Usuń plik: ${f.name}`}
                        disabled={remove.isPending}
                        onClick={() => window.confirm(`Usunąć plik „${f.name}” z wydarzenia?`) && remove.mutate(f)}
                      >
                        Usuń
                      </button>
                    </span>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {adding && (
        <form className="event-files-form" onSubmit={submit}>
          <FileDrop disabled={add.isPending} onFiles={(files) => setFile(files[0] ?? null)}>
            <label className="field">
              <span>Plik</span>
              <input type="file" disabled={add.isPending} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </label>
          </FileDrop>
          <label className="field">
            <span>Opis</span>
            <input type="text" required maxLength={DESCRIPTION_MAX} value={description} placeholder="np. rider techniczny" onChange={(e) => setDescription(e.target.value)} />
          </label>
          <div className="actions">
            <button className="btn small primary" type="submit" disabled={!file || !description.trim() || add.isPending}>
              {add.isPending ? 'Wgrywanie…' : 'Dodaj'}
            </button>
            <button
              className="btn small"
              type="button"
              onClick={() => {
                setAdding(false);
                setFile(null);
                setDescription('');
                add.reset();
              }}
            >
              Anuluj
            </button>
          </div>
          {addError && <p className="alert" role="alert">{addError.message}</p>}
          <p className="hint">Plik trafi do Box jako „opis – data wydarzenia – nazwa wydarzenia”. Dozwolone: pdf, jpg, png, heic, webp, docx, xlsx, csv, txt (do 25 MB).</p>
        </form>
      )}
    </section>
  );
}
